import { Anchor, Button, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import type { ElementType } from 'react';

import type { TextStyledComponentProps } from '../../model/TextStyledComponent';

const TextMap = {
  text: Text,
  paragraph: Paragraph,
} as const;

//  No use case exists yet for Button or TextButton
const CTAMap = {
  anchor: Anchor,
  button: Button,
  textButton: TextButton,
} as const;

type Props = {
  styledComponentContent: TextStyledComponentProps;
  csEventsHandler?: ({ text }: { text: string }) => void;
  csEventsIdMap?: Record<string, string>;
};

// Map v8 fontType to v10 textStyle
const fontTypeToTextStyle: Record<string, string> = {
  h1: 'heading-xl',
  h2: 'heading-lg',
  h3: 'heading-md',
  h4: 'heading-sm',
  h5: 'heading-xs',
  h6: 'body-lg',
  body: 'body',
  bodyLarge: 'body-lg',
  bodySmall: 'body-sm',
  bodyHeading: 'body-bold',
  bodyLargeHeading: 'body-lg-bold',
  bodySmallHeading: 'body-sm-bold',
  finePrint: 'body-xs',
  finePrintHeading: 'body-xs-bold',
  legal: 'fineprint',
};

// Map v8 fontColor to valid color props for Text/Paragraph
const fontColorToColor: Record<string, string> = {
  textWhite: 'text.subtle',
  textDark: 'text.neutral',
  textMedium: 'text.subtle',
  textLight: 'text.subtle',
  textLink: 'text.neutral',
  brand: 'text.neutral',
  brandSecondary: 'text.neutral',
};

// Transform v8 styles to v10 props
const transformStyles = (styles: TextStyledComponentProps['styles']) => {
  const { fontType, fontColor, fontFamily: _fontFamily, ...rest } = styles;

  return {
    ...(fontType ? { textStyle: fontTypeToTextStyle[fontType] ?? fontType } : {}),
    ...(fontColor ? { color: fontColorToColor[fontColor] ?? fontColor } : {}),
    ...rest,
  };
};

export const TextStyledComponent = ({ styledComponentContent, csEventsHandler, csEventsIdMap }: Props) => {
  if (!styledComponentContent.type) {
    styledComponentContent.type = 'text';
  }

  const DynamicTextComponent: ElementType = styledComponentContent.ctaType
    ? CTAMap[styledComponentContent.ctaType as keyof typeof CTAMap]
    : TextMap[styledComponentContent.type as keyof typeof TextMap];

  const anchorProps =
    styledComponentContent.ctaType === 'anchor'
      ? {
          href: styledComponentContent.url,
          target: '_blank',
          onClick: csEventsHandler ? () => csEventsHandler(styledComponentContent) : undefined,
          ['data-eventid']: csEventsIdMap?.[styledComponentContent.text],
        }
      : null;

  const v10Styles = transformStyles(styledComponentContent.styles);

  return (
    <DynamicTextComponent data-testid="TextStyledComponent" {...v10Styles} {...anchorProps}>
      {styledComponentContent.text}
    </DynamicTextComponent>
  );
};
